import {XIcon, FileDiffIcon, CommentIcon, ChevronRightIcon, CopilotIcon} from '@primer/octicons-react'
import {IconButton, Label, Link, RelativeTime} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {clsx} from 'clsx'
import {useCallback, memo, useState, type MouseEvent, type ReactNode} from 'react'
import type {SuggestedChangeLineRange, SuggestionIdentifier} from '@github-ui/diff-lines/conversation-types'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {AutomatedReviewDiffEntry} from '@github-ui/diff-lines/automated-review-diff-entry'
import {SuggestionFileDisclosure} from '@github-ui/diff-lines/suggestion-file-disclosure'
import {useUnifiedBatch, useUnifiedBatchActions} from '../../../stores/layout-selectors'
import {useCommentNavigation} from '../../../hooks/use-comment-navigation'
import type {UnifiedBatchItem} from '../../../stores/slices/unified-batch'
import {ZeroState} from '../ZeroState'
import {restoreFocusOnNextFrame} from '../panel-focus'
import styles from './ManageBatchCommentsList.module.css'

export const MANAGE_BATCH_COMMENTS_HEADING_ID = 'manage-batch-comments-heading'
const REMOVE_BATCH_ITEM_BUTTON_SELECTOR = '[data-remove-batch-item-button]'

/**
 * Shared `Comments(N)` list used by both Manage batch tabs.
 *
 * Each row is a collapsible card. The header lays out sibling controls — a
 * chevron disclosure toggle, the `path:range` link (an anchor into Files
 * changed), an optional `Outdated` label, and the `X` remove control — and the
 * expanded body shows the comment author and the server-rendered comment HTML.
 *
 * Open/closed state is lifted here as a per-row override map so future
 * panel-level interactions can drive every row from one place. Outdated rows
 * default closed.
 */
export function ManageBatchCommentsList() {
  const {items} = useUnifiedBatch()
  const {removeItem} = useUnifiedBatchActions()
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({})

  const handleToggle = useCallback((key: string, currentlyOpen: boolean) => {
    // Derive from the latest override so two activations batched before a re-render
    // (e.g. a rapid double-click) net back to the original state; `currentlyOpen` is
    // only the initial value before the first override exists.
    setOpenOverrides(prev => ({...prev, [key]: !(key in prev ? prev[key] : currentlyOpen)}))
  }, [])

  if (items.length === 0) {
    return (
      <ZeroState
        heading="No comments in batch"
        description="Add a review comment to the batch from any comment's resolve row."
        icon={CommentIcon}
        headingLevel="h4"
      />
    )
  }

  return (
    <ul className={styles.list} data-testid="manage-batch-comments-list">
      {items.map(item => {
        const key = rowKey(item)
        const isOpen = openOverrides[key] ?? !item.isOutdated
        return (
          <ManageBatchCommentRow key={key} item={item} isOpen={isOpen} onToggle={handleToggle} onRemove={removeItem} />
        )
      })}
    </ul>
  )
}

const ManageBatchCommentRow = memo(function ManageBatchCommentRowImpl({
  item,
  isOpen,
  onToggle,
  onRemove,
}: {
  item: UnifiedBatchItem
  isOpen: boolean
  onToggle: (key: string, currentlyOpen: boolean) => void
  onRemove: (id: SuggestionIdentifier) => void
}) {
  const key = rowKey(item)

  const handleToggleClick = useCallback(() => {
    onToggle(key, isOpen)
  }, [key, isOpen, onToggle])

  const handleRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const focusTarget = getFocusTargetAfterBatchItemRemoval(event.currentTarget)

      onRemove({commentDatabaseId: item.commentDatabaseId, suggestionIndex: item.suggestionIndex})
      restoreFocusOnNextFrame(focusTarget)
    },
    [item.commentDatabaseId, item.suggestionIndex, onRemove],
  )

  const commentNavigation = useCommentNavigation({
    commentId: item.commentDatabaseId,
    isOutdated: item.isOutdated,
    sourceUri: item.sourceUri,
    fallbackHref: item.commentUrl,
  })

  const rangeSummary = formatRowRangeSummary(item)
  const rowLabel = rangeSummary ? `${item.path}:${rangeSummary}` : item.path
  const testId = `manage-batch-row-${item.commentDatabaseId}-${suggestionKeyPart(item)}`
  const bodyId = `${testId}-body`

  return (
    <li className={clsx(styles.row, item.isOutdated && styles.rowOutdated)} data-testid={testId}>
      <div className={styles.card} data-testid={`${testId}-card`}>
        {/* The chevron toggle and the path link are siblings (not a single button) so
            the path can be an anchor to the comment in the Files changed view while the
            chevron stays a disclosure control. The remove control follows both, keeping
            DOM/focus order aligned with the visual layout. */}
        <div className={clsx(styles.header, isOpen && styles.headerExpanded)}>
          <IconButton
            icon={ChevronRightIcon}
            variant="invisible"
            size="small"
            aria-expanded={isOpen}
            aria-controls={isOpen ? bodyId : undefined}
            aria-label={isOpen ? `Collapse ${rowLabel}` : `Expand ${rowLabel}`}
            data-testid={`${testId}-toggle`}
            className={clsx(styles.toggle, isOpen && styles.toggleOpen)}
            onClick={handleToggleClick}
          />
          <FileDiffIcon className="fgColor-muted flex-shrink-0" />
          <Link
            className={styles.path}
            href={commentNavigation.href}
            onClick={commentNavigation.onClick}
            data-testid={`${testId}-file-link`}
            aria-label={
              commentNavigation.opensInFilesChanged
                ? `View ${rowLabel} in Files changed`
                : `View comment on ${rowLabel}`
            }
          >
            <span className={styles.pathText}>{item.path}</span>
            {rangeSummary && <span className="fgColor-muted">{`:${rangeSummary}`}</span>}
          </Link>
          {item.isOutdated && (
            <Label variant="attention" size="small">
              Outdated
            </Label>
          )}
          <IconButton
            icon={XIcon}
            aria-label={item.isOutdated ? `Remove ${rowLabel} from batch (outdated)` : `Remove ${rowLabel} from batch`}
            variant="invisible"
            size="small"
            data-remove-batch-item-button
            className={styles.removeButton}
            onClick={handleRemove}
          />
        </div>
        {isOpen && (
          <div className={styles.body} id={bodyId} data-testid={bodyId}>
            <CommentAuthorRow item={item} />
            <CommentBodyContent item={item} commentNavigation={commentNavigation} />
            <SuggestionsSection item={item} />
          </div>
        )}
      </div>
    </li>
  )
})

function CommentAuthorRow({item}: {item: UnifiedBatchItem}) {
  const isCopilot = item.author.isCopilot ?? false
  const createdAt = item.createdAt ? new Date(item.createdAt) : null
  const hasValidCreatedAt = createdAt !== null && !Number.isNaN(createdAt.getTime())
  return (
    <div className={styles.author}>
      <CommentAvatar avatarUrl={item.author.avatarUrl} url={item.author.url} isCopilot={isCopilot} />
      <CommentAuthorName login={item.author.login} url={item.author.url} />
      {hasValidCreatedAt && <RelativeTime className={styles.timestamp} date={createdAt} />}
    </div>
  )
}

/**
 * Author login, linked to the author's profile when the snapshot captured a `url`
 * (a ghost/missing author has none, so it stays plain text).
 */
function CommentAuthorName({login, url}: {login: string; url?: string}) {
  if (!url) return <span className={styles.authorName}>{login}</span>
  return (
    <Link href={url} className={clsx(styles.authorName, styles.authorLink)}>
      {login}
    </Link>
  )
}

function CommentAvatar({avatarUrl, url, isCopilot}: {avatarUrl?: string; url?: string; isCopilot: boolean}) {
  if (isCopilot) {
    return (
      <span className={styles.iconAvatar} aria-hidden="true" data-testid="manage-batch-copilot-avatar">
        <CopilotIcon size={16} />
      </span>
    )
  }
  if (!avatarUrl) return null
  const avatar = <GitHubAvatar src={avatarUrl} alt="" size={28} />
  if (!url) return avatar
  return (
    <Link href={url} tabIndex={-1} aria-hidden="true" className={styles.avatarLink}>
      {avatar}
    </Link>
  )
}

function CommentBodyContent({
  item,
  commentNavigation,
}: {
  item: UnifiedBatchItem
  commentNavigation: ReturnType<typeof useCommentNavigation>
}) {
  const {href, onClick} = commentNavigation

  if (item.bodyTruncated) {
    return (
      <div className={styles.bodyText}>
        <p className="fgColor-muted">Comment too long to preview here.</p>
        <Link
          className="Link--inTextBlock"
          href={href}
          onClick={onClick}
          aria-label={`View full comment by ${item.author.login} on ${item.path}`}
        >
          View full comment
        </Link>
      </div>
    )
  }

  return <VerifiedHTMLDiv className={clsx('markdown-body', styles.bodyText)} html={item.bodyHTML} />
}

/**
 * Per-file suggestion preview shown under the comment body. Both suggestion kinds
 * render inside the same collapsible card chrome ({@link SuggestionFileDisclosure}),
 * so a human and a CCR suggestion read the same in the panel:
 *
 * - `ccr-with-suggestion`: each file's `DiffEntry` via `AutomatedReviewDiffEntry`.
 * - `human-with-suggestion`: the extracted `js-suggested-changes-blob` (chrome
 *   stripped at add-to-batch time) via `VerifiedHTMLDiv`.
 *
 * Each file defaults collapsed.
 */
function SuggestionsSection({item}: {item: UnifiedBatchItem}) {
  const files = suggestionFiles(item)
  if (!files) return null

  return (
    <div className={styles.suggestions} data-testid="manage-batch-suggestions">
      <div className={styles.suggestionsLabel}>Suggestions</div>
      <div className={styles.suggestionFiles}>{files}</div>
    </div>
  )
}

// Layout preset that makes both suggestion kinds render as path-scoped, collapsible,
// screen-reader-labeled cards in the narrow panel.
const PANEL_DISCLOSURE_PROPS = {initialCollapsed: true, pathInLabel: true, wrapPath: true}

function suggestionFiles(item: UnifiedBatchItem): ReactNode {
  if (item.kind === 'ccr-with-suggestion') {
    if (item.suggestionFiles.length === 0) return null
    return item.suggestionFiles.map(diffEntry => (
      <AutomatedReviewDiffEntry key={diffEntry.path} diffEntry={diffEntry} {...PANEL_DISCLOSURE_PROPS} />
    ))
  }

  if (item.kind === 'human-with-suggestion') {
    if (!item.suggestionBlobHTML) return null
    return (
      <SuggestionFileDisclosure path={item.path} {...PANEL_DISCLOSURE_PROPS}>
        <VerifiedHTMLDiv className="markdown-body" html={item.suggestionBlobHTML} />
      </SuggestionFileDisclosure>
    )
  }

  return null
}

function rowKey(item: UnifiedBatchItem): string {
  return `${item.commentDatabaseId}:${suggestionKeyPart(item)}`
}

/**
 * Row-identity suffix for keys/test ids. The store treats `(id, undefined)` and
 * `(id, 0)` as distinct items, so a whole-comment item's `undefined` index must not
 * collapse onto `0` (a plain `?? 0` would give both rows the same key and DOM id).
 */
function suggestionKeyPart(item: UnifiedBatchItem): string {
  return item.suggestionIndex === undefined ? 'comment' : String(item.suggestionIndex)
}

function getFocusTargetAfterBatchItemRemoval(button: HTMLButtonElement) {
  const row = button.closest('li')
  const nextRemoveButton = row?.nextElementSibling?.querySelector<HTMLButtonElement>(REMOVE_BATCH_ITEM_BUTTON_SELECTOR)
  const previousRemoveButton = row?.previousElementSibling?.querySelector<HTMLButtonElement>(
    REMOVE_BATCH_ITEM_BUTTON_SELECTOR,
  )
  return nextRemoveButton ?? previousRemoveButton ?? document.getElementById(MANAGE_BATCH_COMMENTS_HEADING_ID)
}

/**
 * Trailing range summary shown after the path, e.g. `10-12`, `10`, `10-12, 20-25`.
 * Prefers `pathLineRanges` (CCR multi-hunk suggestions carry ranges there with
 * `lineRange: undefined`) and falls back to the single `lineRange`; items with no
 * range (e.g. `human-without-suggestion`) return `''` so the row shows the path alone.
 */
function formatRowRangeSummary(item: UnifiedBatchItem): string {
  const ranges = collectRanges(item)
  if (ranges.length === 0) return ''
  return ranges.map(formatLineRange).join(', ')
}

function collectRanges(item: UnifiedBatchItem): SuggestedChangeLineRange[] {
  // `pathLineRanges` has one entry per file a CCR suggestion touches. The row header
  // is scoped to a single `item.path`, so surface only that file's ranges to avoid
  // attributing another file's ranges to this row and its remove-button label. Falls
  // back to the single `lineRange` when no per-path ranges match.
  if (item.pathLineRanges && item.pathLineRanges.length > 0) {
    const rangesForPath = item.pathLineRanges.filter(entry => entry.path === item.path).map(entry => entry.lineRange)
    if (rangesForPath.length > 0) return rangesForPath
  }
  return item.lineRange ? [item.lineRange] : []
}

function formatLineRange(range: SuggestedChangeLineRange): string {
  return range.startLineNumber === range.endLineNumber
    ? `${range.startLineNumber}`
    : `${range.startLineNumber}-${range.endLineNumber}`
}
